import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContentEntityManager,
  type ContentEntityConfig,
} from '@/pages/settings/ContentEntityManager';
import {
  createFaqEntry,
  createParentOpinion,
  deleteFaqEntry,
  deleteParentOpinion,
  listFaqEntries,
  listParentOpinions,
  reorderFaqEntries,
  reorderParentOpinions,
  updateFaqEntry,
  updateParentOpinion,
} from '@/api/websiteContent.api';
import type {
  FaqEntry,
  FaqEntryUpsertRequest,
  ParentOpinion,
  ParentOpinionUpsertRequest,
} from '@/types/shared';
import './ContentSettingsPage.css';

/**
 * Parents' Opinions manager config (T140/DEC-WC2). Length caps mirror
 * `ParentOpinionUpsertDto` (T139, §5 Assumption A) exactly: name/role 150, quote 1000.
 */
const PARENT_OPINION_CONFIG: ContentEntityConfig<ParentOpinion> = {
  i18nKey: 'parentOpinions',
  fields: [
    { key: 'name', maxLength: 150 },
    { key: 'role', maxLength: 150 },
    { key: 'quote', maxLength: 1000, multiline: true },
  ],
  api: {
    list: () => listParentOpinions(),
    // Safe: the manager only ever builds `payload` from exactly this config's fields
    // (`nameAr/En`, `roleAr/En`, `quoteAr/En`), i.e. the full `ParentOpinionUpsertRequest` shape.
    create: (payload) => createParentOpinion(payload as unknown as ParentOpinionUpsertRequest),
    update: (id, payload) =>
      updateParentOpinion(id, payload as unknown as ParentOpinionUpsertRequest),
    remove: (id) => deleteParentOpinion(id),
    reorder: (ids) => reorderParentOpinions(ids),
  },
  summary: (row, language) => (language.startsWith('ar') ? row.nameAr : row.nameEn),
};

/**
 * FAQ manager config (T140/DEC-WC2). Length caps mirror `FaqEntryUpsertDto`: question 300,
 * answer 2000.
 */
const FAQ_ENTRY_CONFIG: ContentEntityConfig<FaqEntry> = {
  i18nKey: 'faq',
  fields: [
    { key: 'question', maxLength: 300 },
    { key: 'answer', maxLength: 2000, multiline: true },
  ],
  api: {
    list: () => listFaqEntries(),
    create: (payload) => createFaqEntry(payload as unknown as FaqEntryUpsertRequest),
    update: (id, payload) => updateFaqEntry(id, payload as unknown as FaqEntryUpsertRequest),
    remove: (id) => deleteFaqEntry(id),
    reorder: (ids) => reorderFaqEntries(ids),
  },
  summary: (row, language) => {
    const question = language.startsWith('ar') ? row.questionAr : row.questionEn;
    return question.length > 60 ? `${question.slice(0, 60)}…` : question;
  },
};

type TabKey = 'parentOpinions' | 'faq';

interface TabDef {
  key: TabKey;
  labelKey: string;
}

const TABS: TabDef[] = [
  { key: 'parentOpinions', labelKey: 'contentSettings.tabs.parentOpinions' },
  { key: 'faq', labelKey: 'contentSettings.tabs.faq' },
];

/**
 * Settings → Content (`/settings/content`, Admin-only — gated by `RoleGuard` in `router.tsx`,
 * DEC-WC1/DEC-WC7). Standard ARIA tabs pattern reused verbatim from `LookupsPage.tsx`
 * (10-design-system.md §4.9): Parents' Opinions | FAQ, only the active tabpanel mounted. Each
 * panel is the same `ContentEntityManager`, driven by a per-entity field/API config.
 */
export function ContentSettingsPage() {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const isRtl = i18n.language.startsWith('ar');

  function focusTab(index: number) {
    const normalized = (index + TABS.length) % TABS.length;
    setActiveIndex(normalized);
    tabRefs.current[normalized]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(index + (isRtl ? -1 : 1));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(index + (isRtl ? 1 : -1));
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(TABS.length - 1);
        break;
      default:
        break;
    }
  }

  const activeTab = TABS[activeIndex];

  return (
    <section className="fa-content-settings-page">
      <div className="fa-content-settings-page__header">
        <h1 className="fa-content-settings-page__title">{t('contentSettings.pageTitle')}</h1>
        <p className="fa-content-settings-page__description">
          {t('contentSettings.pageDescription')}
        </p>
      </div>

      <div
        role="tablist"
        aria-label={t('contentSettings.tabsAriaLabel')}
        className="fa-content-settings-page__tablist"
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`content-settings-tab-${tab.key}`}
            aria-selected={activeIndex === index}
            aria-controls={`content-settings-tabpanel-${tab.key}`}
            tabIndex={activeIndex === index ? 0 : -1}
            className={`fa-content-settings-page__tab ${
              activeIndex === index ? 'fa-content-settings-page__tab--active' : ''
            }`}
            onClick={() => setActiveIndex(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`content-settings-tabpanel-${activeTab.key}`}
        aria-labelledby={`content-settings-tab-${activeTab.key}`}
        tabIndex={0}
        className="fa-content-settings-page__tabpanel"
      >
        {activeTab.key === 'parentOpinions' ? (
          <ContentEntityManager key="parentOpinions" config={PARENT_OPINION_CONFIG} />
        ) : (
          <ContentEntityManager key="faq" config={FAQ_ENTRY_CONFIG} />
        )}
      </div>
    </section>
  );
}
